import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";

import {
  AdminFormSection,
  AdminFormToolbar,
} from "../../../pages/admin/section_engine";

import {
  AdminButton,
} from "../../../pages/admin/action_engine";


const CHANNEL_OPTIONS = [
  "api",
  "sftp",
  "email",
  "portal",
];

const QUALIFICATION_OPTIONS = [
  "unknown",
  "unqualified",
  "qualified",
  "rejected",
];

const CERTIFICATION_OPTIONS = [
  "uncertified",
  "pending",
  "certified",
  "failed",
];


function normalizeFormats(value) {
  if (!Array.isArray(value)) {
    return "";
  }

  return value.join(", ");
}


function createInitialState(capability) {
  return {
    channel:
      capability?.channel || "email",

    supportedFormats:
      normalizeFormats(
        capability?.supported_formats,
      ),

    isCurrent:
      Boolean(capability?.is_current),

    isTarget:
      Boolean(capability?.is_target),

    isPreferred:
      Boolean(
        capability?.is_preferred,
      ),

    fallbackPriority:
      capability?.fallback_priority
        ?? "",

    qualificationStatus:
      capability?.qualification_status
      || "unknown",

    certificationStatus:
      capability?.certification_status
      || "uncertified",
  };
}


function buildPayload(state) {
  const formats = state.supportedFormats
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

  const fallbackPriority =
    state.fallbackPriority === ""
      ? null
      : Number(
          state.fallbackPriority,
        );

  return {
    channel: state.channel,
    supported_formats: formats,
    is_current: state.isCurrent,
    is_target: state.isTarget,
    is_preferred:
      state.isPreferred,
    fallback_priority:
      fallbackPriority,
    qualification_status:
      state.qualificationStatus,
    certification_status:
      state.certificationStatus,
  };
}


export default function SupplierCapabilityForm({
  capability = null,
  submitting = false,
  onSubmit,
  onCancel,
}) {
  const [state, setState] = useState(
    () => createInitialState(
      capability,
    ),
  );

  useEffect(() => {
    setState(
      createInitialState(
        capability,
      ),
    );
  }, [capability]);

  const title = useMemo(
    () => (
      capability
        ? "Modifier la capacité"
        : "Ajouter une capacité"
    ),
    [capability],
  );

  const updateField = (
    fieldName,
    value,
  ) => {
    setState((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async (
    event,
  ) => {
    event.preventDefault();

    if (typeof onSubmit !== "function") {
      return;
    }

    await onSubmit(
      buildPayload(state),
    );
  };

  return (
    <AdminFormSection
      data-testid="supplier-capability-form"
    >
      <FormTitle>
        {title}
      </FormTitle>

      <AdminFormToolbar
        onSubmit={handleSubmit}
      >
        <FieldsGrid>
          <Field>
            <FieldLabel
              htmlFor="supplier-capability-channel"
            >
              Canal
            </FieldLabel>

            <FieldSelect
              id="supplier-capability-channel"
              value={state.channel}
              onChange={(event) => {
                updateField(
                  "channel",
                  event.target.value,
                );
              }}
            >
              {CHANNEL_OPTIONS.map(
                (channel) => (
                  <option
                    key={channel}
                    value={channel}
                  >
                    {channel.toUpperCase()}
                  </option>
                ),
              )}
            </FieldSelect>
          </Field>

          <Field>
            <FieldLabel
              htmlFor="supplier-capability-formats"
            >
              Formats supportés
            </FieldLabel>

            <FieldInput
              id="supplier-capability-formats"
              value={
                state.supportedFormats
              }
              placeholder="json, csv"
              onChange={(event) => {
                updateField(
                  "supportedFormats",
                  event.target.value,
                );
              }}
            />
          </Field>

          <Field>
            <FieldLabel
              htmlFor="supplier-capability-qualification"
            >
              Qualification
            </FieldLabel>

            <FieldSelect
              id="supplier-capability-qualification"
              value={
                state.qualificationStatus
              }
              onChange={(event) => {
                updateField(
                  "qualificationStatus",
                  event.target.value,
                );
              }}
            >
              {QUALIFICATION_OPTIONS.map(
                (status) => (
                  <option
                    key={status}
                    value={status}
                  >
                    {status}
                  </option>
                ),
              )}
            </FieldSelect>
          </Field>

          <Field>
            <FieldLabel
              htmlFor="supplier-capability-certification"
            >
              Certification
            </FieldLabel>

            <FieldSelect
              id="supplier-capability-certification"
              value={
                state.certificationStatus
              }
              onChange={(event) => {
                updateField(
                  "certificationStatus",
                  event.target.value,
                );
              }}
            >
              {CERTIFICATION_OPTIONS.map(
                (status) => (
                  <option
                    key={status}
                    value={status}
                  >
                    {status}
                  </option>
                ),
              )}
            </FieldSelect>
          </Field>

          <Field>
            <FieldLabel
              htmlFor="supplier-capability-fallback"
            >
              Priorité fallback
            </FieldLabel>

            <FieldInput
              id="supplier-capability-fallback"
              type="number"
              min="1"
              value={
                state.fallbackPriority
              }
              onChange={(event) => {
                updateField(
                  "fallbackPriority",
                  event.target.value,
                );
              }}
            />
          </Field>
        </FieldsGrid>

        <Flags>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={state.isCurrent}
              onChange={(event) => {
                updateField(
                  "isCurrent",
                  event.target.checked,
                );
              }}
            />
            Canal actuel
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={state.isTarget}
              onChange={(event) => {
                updateField(
                  "isTarget",
                  event.target.checked,
                );
              }}
            />
            Canal cible
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={
                state.isPreferred
              }
              onChange={(event) => {
                updateField(
                  "isPreferred",
                  event.target.checked,
                );
              }}
            />
            Canal préféré
          </CheckboxLabel>
        </Flags>

        <Actions>
          <AdminButton
            type="submit"
            variant="primary"
            disabled={submitting}
          >
            {submitting
              ? "Enregistrement..."
              : "Enregistrer"}
          </AdminButton>

          <AdminButton
            type="button"
            variant="ghost"
            disabled={submitting}
            onClick={onCancel}
          >
            Annuler
          </AdminButton>
        </Actions>
      </AdminFormToolbar>
    </AdminFormSection>
  );
}


const FormTitle = styled.h4`
  margin: 0 0 12px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns:
    repeat(
      auto-fit,
      minmax(220px, 1fr)
    );
  gap: 12px;
  width: 100%;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-weight: 600;
`;

const FieldInput = styled.input`
  width: 100%;
  box-sizing: border-box;
`;

const FieldSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
`;

const Flags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;
